import React, { useState, useEffect } from 'react';

import { CARD_DECKS } from '../utils/roomUtils';
import { CardDeckType } from '../utils/types';

interface CardDeckSelectorProps {
    currentDeckType: CardDeckType;
    currentValues: string[];
    onUpdateDeck: (deckType: CardDeckType, customValues?: string[]) => void;
}

const CardDeckSelector: React.FC<CardDeckSelectorProps> = ({
    currentDeckType,
    currentValues,
    onUpdateDeck,
}) => {
    const [deckType, setDeckType] = useState<CardDeckType>(currentDeckType);
    const [customValues, setCustomValues] = useState<string>(
        currentDeckType === 'custom' ? currentValues.join(', ') : '',
    );
    const [showCustomInput, setShowCustomInput] = useState<boolean>(
        currentDeckType === 'custom',
    );
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

    useEffect(() => {
        if (saveSuccess) {
            const timer = setTimeout(() => {
                setSaveSuccess(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [saveSuccess]);

    const handleDeckTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as CardDeckType;
        setDeckType(newType);
        setShowCustomInput(newType === 'custom');
        setIsDirty(true);

        if (newType !== 'custom') {
            handleDeckSave(newType);
        }
    };

    const handleCustomValuesChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setCustomValues(e.target.value);
        setIsDirty(true);
    };

    const handleDeckSave = (type: CardDeckType = deckType) => {
        if (type === 'custom') {
            const values = Array.from(
                new Set(
                    customValues
                        .split(',')
                        .map((val) => val.trim())
                        .filter((val) => val.length > 0),
                ),
            );

            if (values.length === 0) {
                alert('Please enter at least one valid card value');
                return;
            }

            setIsSaving(true);
            try {
                onUpdateDeck('custom', values);
                setSaveSuccess(true);
                setIsDirty(false);
            } catch (error) {
                console.error('Failed to save custom deck:', error);
                alert('Failed to save custom deck. Please try again.');
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsSaving(true);
            try {
                onUpdateDeck(type);
                setSaveSuccess(true);
                setIsDirty(false);
            } catch (error) {
                console.error('Failed to update deck:', error);
                alert('Failed to update deck. Please try again.');
            } finally {
                setIsSaving(false);
            }
        }
    };

    const renderCardPreviews = () => {
        const previewValues =
            deckType === 'custom'
                ? Array.from(
                      new Set(
                          customValues
                              .split(',')
                              .map((val) => val.trim())
                              .filter((val) => val.length > 0),
                      ),
                  )
                : CARD_DECKS[deckType];

        return (
            <div className="flex flex-wrap gap-2 mt-3 justify-center">
                {previewValues.length > 0 ? (
                    previewValues.map((value, index) => (
                        <div
                            key={index}
                            className="px-3 py-2 bg-white dark:bg-dark-card shadow-sm rounded-md border border-gray-200 dark:border-dark-border text-center font-medium dark:text-dark-text"
                        >
                            {value}
                        </div>
                    ))
                ) : (
                    <p className="text-gray-500 dark:text-gray-400 italic">
                        No card values defined
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 border border-gray-200 dark:border-dark-border shadow-sm">
            <div className="mb-4">
                <label
                    htmlFor="deckType"
                    className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                >
                    Card Deck Type
                </label>
                <select
                    id="deckType"
                    value={deckType}
                    onChange={handleDeckTypeChange}
                    className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-dark-border text-gray-900 dark:text-dark-text rounded-md focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
                >
                    <option value="fibonacci">
                        Fibonacci (0, 1, 2, 3, 5, 8, 13, 21, 34, ?)
                    </option>
                    <option value="bau">
                        BAU (1, 2, 3, 4, 5, 6, 7, 8, 9, 10)
                    </option>
                    <option value="tshirt">
                        T-Shirt Sizes (XXS, XS, S, M, L, XL, ?)
                    </option>
                    <option value="powers">
                        Powers of 2 (0, 1, 2, 4, 8, 16, 32, 64, ?)
                    </option>
                    <option value="custom">Custom Values</option>
                </select>
            </div>

            {showCustomInput && (
                <div className="mb-4">
                    <label
                        htmlFor="customValues"
                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                    >
                        Custom Card Values (comma-separated)
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="customValues"
                            type="text"
                            value={customValues}
                            onChange={handleCustomValuesChange}
                            placeholder="1, 2, 3, 5, 8, 13, ?"
                            className="flex-1 p-2 border border-gray-300 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text rounded-md focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
                        />
                        <button
                            onClick={() => handleDeckSave()}
                            disabled={isSaving || !isDirty}
                            className={`
                                px-4 py-2 rounded-md font-medium transition-colors duration-200
                                ${isSaving ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : isDirty ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'}
                            `}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Enter values separated by commas. Include "?" for
                        unknown estimates.
                    </p>
                </div>
            )}

            <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-md border border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Card Preview
                </h4>
                {renderCardPreviews()}
            </div>

            {saveSuccess && (
                <div className="mt-3 py-2 px-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm flex items-center rounded-md border border-green-100 dark:border-green-800">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                    Card deck updated successfully!
                </div>
            )}
        </div>
    );
};

export default CardDeckSelector;
